import { useContext, useMemo } from 'react';
import ThreadInputBoxContext from 'src/contexts/ThreadInputBoxContext';
import { AppRoutes, QuickSkillCommand } from 'src/types';
import ForwardRefContext from 'src/contexts/ForwardRefContext';
import {
  useSkillCommand,
  useComparingConversationsParams,
  useGetUser,
} from 'src/hooks';
import { Button } from 'src/v2/commonComponents/Button';
import { Icon as IconComponent } from '@phosphor-icons/react';
import { SVG_SIZE_M } from 'src/constants';
import styles from './SkillCommandToggle.module.scss';
import AuthContext from 'src/contexts/AuthContext';

interface SkillCommandToggleProps {
  command: QuickSkillCommand;
  title: string;
  tooltipContent: string;
  Icon: IconComponent;
  fullWidth?: boolean;
  onClick: () => void;
}

export const SkillCommandToggle = ({
  command,
  title,
  tooltipContent,
  Icon,
  fullWidth,
  onClick,
}: SkillCommandToggleProps) => {
  const { isCompareMode } = useComparingConversationsParams();
  const { threadInputBoxRef } = useContext(ForwardRefContext);
  const { threadInputBoxFiles } = useContext(ThreadInputBoxContext);
  const { isGuestAccess } = useContext(AuthContext);

  const {
    isUltraTier,
    isUltraTrialTier,
    isTeamsUltraTier,
    isEnterpriseTier,
    isEnterpriseTrialTier,
    isSuperNinjaUltraTier,
    isSuperNinjaUltraTrialTier,
    isTeamsMaxTier,
    isMaxTier,
  } = useGetUser();

  const attachedFilesLength = threadInputBoxFiles
    ? Object.keys(threadInputBoxFiles).length
    : 0;

  const { toggleActiveSkillCommand } = useSkillCommand();

  const isThinkCommand = useMemo(
    () => command === QuickSkillCommand.THINKING,
    [command],
  );

  const isDisable = useMemo(() => {
    if (
      attachedFilesLength > 0 &&
      command !== QuickSkillCommand.DEEP_RESEARCHER &&
      command !== QuickSkillCommand.IMAGE
    ) {
      return true;
    }

    if (
      isCompareMode &&
      (command === QuickSkillCommand.DEEP_RESEARCHER ||
        command === QuickSkillCommand.THINKING ||
        command === QuickSkillCommand.IMAGE)
    ) {
      return true;
    }

    if (
      isThinkCommand &&
      !(
        isUltraTier ||
        isUltraTrialTier ||
        isTeamsUltraTier ||
        isEnterpriseTier ||
        isEnterpriseTrialTier ||
        isSuperNinjaUltraTier ||
        isSuperNinjaUltraTrialTier ||
        isTeamsMaxTier ||
        isMaxTier
      )
    ) {
      return true;
    }

    return false;
  }, [
    attachedFilesLength,
    command,
    isCompareMode,
    isThinkCommand,
    isUltraTier,
    isUltraTrialTier,
    isTeamsUltraTier,
    isEnterpriseTier,
    isEnterpriseTrialTier,
    isSuperNinjaUltraTier,
    isSuperNinjaUltraTrialTier,
    isTeamsMaxTier,
    isMaxTier,
  ]);

  const handleToggleClick = () => {
    if (isGuestAccess) {
      window.location.href = AppRoutes.LOGIN;
      return;
    }

    toggleActiveSkillCommand(command);

    threadInputBoxRef?.current?.focus();
    onClick();
  };

  return (
    <Button
      shape="regular"
      color={fullWidth ? 'transparent' : 'secondary'}
      onClick={handleToggleClick}
      tooltipContent={tooltipContent}
      fullWidth={fullWidth}
      disabled={isDisable}
      className={styles.root}
    >
      <span className={styles.title}>
        <Icon size={SVG_SIZE_M} />
        <span>{title}</span>
      </span>
    </Button>
  );
};
